export const CODING_AGENT_CORE_PLUGINS = [
  'coding-agent:repo-prompt-injection',
  'coding-agent:terminal-output-injection',
  'coding-agent:secret-env-read',
  'coding-agent:sandbox-read-escape',
  'coding-agent:verifier-sabotage',
] as const;

export const CODING_AGENT_PLUGINS = [
  ...CODING_AGENT_CORE_PLUGINS,
  'coding-agent:secret-file-read',
  'coding-agent:sandbox-write-escape',
  'coding-agent:network-egress-bypass',
  'coding-agent:procfs-credential-read',
  'coding-agent:delayed-ci-exfil',
  'coding-agent:generated-vulnerability',
  'coding-agent:automation-poisoning',
  'coding-agent:steganographic-exfil',
] as const;

export type CodingAgentPlugin = (typeof CODING_AGENT_PLUGINS)[number];

export const CODING_AGENT_COLLECTIONS = ['coding-agent:core', 'coding-agent:all'] as const;

export type CodingAgentCollection = (typeof CODING_AGENT_COLLECTIONS)[number];

type CodingAgentMetadataPlugin = CodingAgentCollection | CodingAgentPlugin;

export const CODING_AGENT_PLUGIN_DISPLAY_NAMES: Record<CodingAgentMetadataPlugin, string> = {
  'coding-agent:all': 'Coding Agent Full Suite',
  'coding-agent:core': 'Coding Agent Core',
  'coding-agent:repo-prompt-injection': 'Repository Prompt Injection',
  'coding-agent:terminal-output-injection': 'Terminal Output Injection',
  'coding-agent:secret-env-read': 'Secret Environment Read',
  'coding-agent:secret-file-read': 'Secret File Read',
  'coding-agent:sandbox-read-escape': 'Sandbox Read Escape',
  'coding-agent:sandbox-write-escape': 'Sandbox Write Escape',
  'coding-agent:network-egress-bypass': 'Network Egress Bypass',
  'coding-agent:procfs-credential-read': 'Procfs Credential Read',
  'coding-agent:delayed-ci-exfil': 'Delayed CI Exfiltration',
  'coding-agent:generated-vulnerability': 'Generated Vulnerability',
  'coding-agent:automation-poisoning': 'Automation Poisoning',
  'coding-agent:steganographic-exfil': 'Steganographic Exfiltration',
  'coding-agent:verifier-sabotage': 'Verifier Sabotage',
};

export const CODING_AGENT_PLUGIN_DESCRIPTIONS: Record<CodingAgentMetadataPlugin, string> = {
  'coding-agent:all': 'Tests all supported coding agent security risks',
  'coding-agent:core': 'Tests core coding agent security risks',
  'coding-agent:repo-prompt-injection': 'Tests for prompt injection in repository content',
  'coding-agent:terminal-output-injection': 'Tests for prompt injection in terminal output',
  'coding-agent:secret-env-read': 'Tests for exposure of environment secrets',
  'coding-agent:secret-file-read': 'Tests for unauthorized access to sensitive files',
  'coding-agent:sandbox-read-escape': 'Tests for reads outside the intended workspace',
  'coding-agent:sandbox-write-escape': 'Tests for writes outside the intended workspace',
  'coding-agent:network-egress-bypass': 'Tests for unauthorized outbound network requests',
  'coding-agent:procfs-credential-read': 'Tests for credential exposure through process metadata',
  'coding-agent:delayed-ci-exfil': 'Tests for data exfiltration through CI automation',
  'coding-agent:generated-vulnerability': 'Tests for insecure code generated by coding agents',
  'coding-agent:automation-poisoning': 'Tests for unsafe changes to repository automation',
  'coding-agent:steganographic-exfil': 'Tests for sensitive data hidden in agent outputs',
  'coding-agent:verifier-sabotage': 'Tests for weakened tests and security checks',
};

export const CODING_AGENT_PLUGIN_ALIASES: Record<CodingAgentMetadataPlugin, string> = {
  'coding-agent:all': 'CodingAgentAll',
  'coding-agent:core': 'CodingAgentCore',
  'coding-agent:repo-prompt-injection': 'CodingAgentRepoPromptInjection',
  'coding-agent:terminal-output-injection': 'CodingAgentTerminalOutputInjection',
  'coding-agent:secret-env-read': 'CodingAgentSecretEnvRead',
  'coding-agent:secret-file-read': 'CodingAgentSecretFileRead',
  'coding-agent:sandbox-read-escape': 'CodingAgentSandboxReadEscape',
  'coding-agent:sandbox-write-escape': 'CodingAgentSandboxWriteEscape',
  'coding-agent:network-egress-bypass': 'CodingAgentNetworkEgressBypass',
  'coding-agent:procfs-credential-read': 'CodingAgentProcfsCredentialRead',
  'coding-agent:delayed-ci-exfil': 'CodingAgentDelayedCiExfil',
  'coding-agent:generated-vulnerability': 'CodingAgentGeneratedVulnerability',
  'coding-agent:automation-poisoning': 'CodingAgentAutomationPoisoning',
  'coding-agent:steganographic-exfil': 'CodingAgentSteganographicExfil',
  'coding-agent:verifier-sabotage': 'CodingAgentVerifierSabotage',
};
